import React, { useContext } from "react";
import { Pause, Play, RotateCcw, Square, X } from "lucide-react";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../ui/alert_dialog";
import { TestContext } from "../../context/test_context";

interface TestActionButtonsProps {
  testgroup: any;
  onTogglePlayPause: () => Promise<void>;
  onStop?: () => Promise<void>;
  onRestart?: () => Promise<void>;
  onDelete: () => Promise<void>;
}

function ActionButton({
  loading,
  onClick,
  children,
  variant = "default",
}: {
  loading: boolean;
  onClick: () => void;
  children: React.ReactNode;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
}) {
  return (
    <Button variant={variant} onClick={onClick} disabled={loading} className="min-w-[90px] gap-2">
      {loading ? <Spinner size="sm" /> : children}
    </Button>
  );
}

export default function TestActionButtons({
  testgroup,
  onTogglePlayPause,
  onStop,
  onRestart,
  onDelete,
}: TestActionButtonsProps) {
  const { pauseLoading, restartLoading, stopLoading } = useContext(TestContext);

  if (!testgroup) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {!testgroup.finished ? (
        <ActionButton loading={pauseLoading} onClick={onTogglePlayPause}>
          {testgroup.active ? (
            <>
              <Pause className="h-4 w-4" />
              Pause
            </>
          ) : (
            <>
              <Play className="h-4 w-4" />
              Play
            </>
          )}
        </ActionButton>
      ) : (
        <ActionButton loading={restartLoading} onClick={onRestart ?? (() => {})}>
          <RotateCcw className="h-4 w-4" />
          Restart
        </ActionButton>
      )}

      {!testgroup.finished && onStop && (
        <ActionButton loading={stopLoading} onClick={onStop}>
          <Square className="h-4 w-4" />
          Stop
        </ActionButton>
      )}

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="destructive" className="min-w-[90px] gap-2">
            <X className="h-4 w-4" />
            Delete
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this test group?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. All test data for this group will be permanently
              removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
